import { useEffect } from 'react';
import { useTenantBranding, DEFAULT_BRAND_NAME } from '@/hooks/use-tenant-branding';
import { useAuthStore } from '@/stores/auth.store';

/** Matches the static <title> in index.html. */
const DEFAULT_FAVICON = '/favicon.ico';
const MANAGED_ICON_ID = 'tenant-favicon';

/**
 * Applies tenant branding to the document head: title and favicon.
 *
 * Renders nothing — it exists purely for the side effect, so that the sidebar
 * and footer stay pure presentational components.
 *
 * Isolation is the reason this keys on `isAuthenticated` rather than on the
 * branding data alone: react-query keeps cached data after logout, so without
 * an explicit reset the previous tenant's name and favicon would remain on the
 * login page and be inherited by whoever logs in next on the same browser.
 */
export function TenantBrandingEffect() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { brandName, faviconUrl } = useTenantBranding();

  const activeName = isAuthenticated ? brandName : DEFAULT_BRAND_NAME;
  const activeIcon = isAuthenticated ? faviconUrl : null;

  useEffect(() => {
    document.title = activeName;
  }, [activeName]);

  useEffect(() => {
    // index.html ships no <link rel="icon">, so the element is created on first
    // use and reused thereafter. It is tagged with an id so this component only
    // ever touches the icon it owns.
    let link = document.getElementById(MANAGED_ICON_ID) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.id = MANAGED_ICON_ID;
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = activeIcon ?? DEFAULT_FAVICON;
  }, [activeIcon]);

  return null;
}
